// These are types that can be declared to help enforce API <-> Client contracts, and
//  help with the debugging and ongoing maintenance of those calls.
import { AuthRoles } from '../acl/acl-i';
import { IPublisher } from '../article/publishers';
import { IClient } from '../client/client-i';
import { IBulkSynonymsInsert, IBulkSynonymsTest } from '../marker/synonym';
import { ISymbolMatches } from '../market/symbols';
import { ISpacyInterpret } from '../spacy';
import { IStemResponse } from '../stemWord';

export const DEFAULT_COLLECTION = 'Default';

export const COMPRESSED_FILE_SUFFIX = '.gz';

// Useful for turning paragraph breaks into sentence breaks or just adding more breaks
// The end result is text without extra viewable/parsable punctuation
export const FORCED_SENTENCE_TERMINATOR = '\x1f';
export const FORCED_TERMINATOR_REPLACEMENT = ' ';

// Track existing versions of table spec storage
// If we have tables in production and need to update the spec, this
//  will help us know which versions are outstanding and need to be handled.
export enum TABLE_PARSE_VERSION {
    v6 = 'v6',   // Article with title text only in table data
    v6b = 'v6b', // Pay attention to empty paragraphs. (writing out article sentence)
    v6c = 'v6c', // Handle malformed(sparse) tables where column counts differ between rows
    v6d = 'v6d', // Table pseudo paragraph support and alternate title derivation (previous paragraph & single cell table rows)
    v6e = 'v6e', // Ignore unneeded colspans instead of duplicating
}

export const ALL_SELECTIONS = '_all';

// General Create/Update Contracts
// Return type for communicating success in creating new data
export interface ICreate {
    newRowId: number;
}

// Return type for communicating success in updating data
export interface IUpdate {
    rowsUpdated: number;
}

export const exampleUpdate: IUpdate = {
    rowsUpdated: 4,
};

// API DOC Contracts
type TEndpointList = {
    [pathPrefix: string]: { [methodAndSuffix: string]: [] },
};
export type TRoutes = {
    endpointPrefix: string;
    endpoints: TEndpointList;
};

export interface IApiDoc {
    responseBody: any;
    url: string;             // http://localhost:3000/knowledge/marker/1
    description: string;     // Get the generic parts of a marker by id. This API...
    method: string;          // get
    path: string;            // /knowledge/markerTree/:type
    requiredFields: string;  // ['id'] OR { 'id': VALIDATION_TYPE.NUMERIC }
    optionalFields?: string; // ['type'] OR { 'type': VALIDATION_TYPE.ALPHA_NUMERIC }
    curl: string;            // Generated: curl -X GET http://...
    requestBody: any;
    interfaceCode?: string;
    constCode?: string;
}

export const exampleApiDoc: IApiDoc[] = [
    {
        method: 'post',
        path: '/ops/apiDoc',
        requiredFields: '{ \'api\':\'STRING\'}',
        description: 'Get runtime reporting and validation of discoverable APIs',
        requestBody: {
            api: 'STRING',
        },
        curl: `curl -X GET http://localhost:3000/ops/apiDoc -H "{ authorization: ...}" -D "{ someParam: '/knowledge' }"`,
        url: 'http://localhost:3000/ops/apiDoc',
        responseBody: {
            message: 'To avoid possible mutated data, this API code was not run.',
        },
    },
];

export const exampleResponseCopyPortfolio = true;

// Client Contracts
export const exampleClient: IClient & { created: string; creator: string } = {
    clientName: 'causalitylink',
    created: '2021-10-21T22:30:34.248Z',
    creator: 'bob@causalitylink.com',
    label: 'CausalityLink',
    subscribedPublishers: [ 'nn' ],
    rolesList: [ AuthRoles.CLAdmin, AuthRoles.KnowledgeRead ],
    lastUpdated: '2021-10-21T22:30:34.248Z',
};

export const examplePublisher: IPublisher & { created: string; creator: string } = {
    publisherName: 'causalitylink',
    created: '2021-10-21T22:30:34.248Z',
    creator: 'bob@causalitylink.com',
    label: 'CausalityLink',
    lastUpdated: '2021-10-21T22:30:34.248Z',
    hostname: 'cl',
};

export const examplePublisherDeleted: IPublisher & { created: string; creator: string } = {
    publisherName: 'causalitylink',
    created: '2021-10-21T22:30:34.248Z',
    creator: 'bob@causalitylink.com',
    label: 'CausalityLink',
    lastUpdated: '2021-10-21T22:30:34.248Z',
    hostname: 'cl',
    deleted: '2021-10-21T22:30:34.248Z',
};

export const exampleNewAuthor = {
    id: 63380,
    marker: 'test_marker',
    parentId: null,
    label: 'test Marker',
    created: '2021-12-29T16:57:13.000Z',
    updated: '2021-12-29T16:57:13.000Z',
    deleted: null,
    creator: 'aldo.martinez@causalitylink.com',
    actor: null,
    markerStatus: 0,
    markerType: 'author',
    container: 0,
    isDisplay: 0,
    depth: null,
    abbreviation: null,
    mentionCount: 0,
    childrenMentionCount: 0,
    description: null,
    terms: null,
    markerId: 63380,
    url: null,
    authorType: 0,
    type: 'author',
    synonyms: null,
    synonymIds: null,
    samples: [ 'NOSAMPLE' ],
    synonymCount: 0,
    orderDate: '2021-12-29T16:57:13.000Z',
    associations: null,
    associationIds: null,
    associationSamples: null,
    parentLabel: null,
    parentForDelete: 0,
    xi: null,
    hasLabelSynonym: false,
    notCreator: false,
};

// Knowledge Contracts

export const exampleNewMarkerCandidate = '8cc84227-2f27-48a2-9fdf-00b99615c579';
export const exampleTrue = true;
export const exampleEmpty = {};

// list with the ids in order
export const exampleCollectionChange = [ 3, 372014, 372015, 372016 ];

// example marker can be any marker type
export const exampleConvertToContainer = {
    id: 63380,
    marker: 'test_marker',
    parentId: null,
    label: 'test Marker',
    created: '2021-12-29T16:57:13.000Z',
    updated: '2021-12-29T16:57:13.000Z',
    deleted: null,
    creator: 'aldo.martinez@causalitylink.com',
    actor: 'aldo.martinez@causalitylink.com',
    markerStatus: 0,
    markerType: 'author',
    container: 1,
    isDisplay: 0,
    depth: null,
    abbreviation: null,
    mentionCount: 0,
    childrenMentionCount: 0,
    description: null,
    terms: null,
};

export const NOVELTY_REPORT_DELIMITER = '|';

export const exampleSuccess = { success: true, apiVersion: 2 };

export const exampleParseAttempt = {
    success: true,
    softwareVersion: 'V3.6.0',
    knowledgeVersion: '2023-04-20 02:58',
    parseTime: 1686602673735,
};

export const exampleDaasConcept = [
    {
        concept: 'apple',
        date: '2023-06-12',
        topics: [
            'stock_price',
            'profit',
            'capital_return',
            'revenue',
        ],
        current_indicator: 203,
        current_event: 37,
        outlook_indicator: 31,
        outlook_event: 4,
        up_current: 41,
        down_current: 8,
        positive_current_indicator: 41,
        positive_current_event: 11,
        negative_current_indicator: 8,
        negative_current_event: 5,
        flat_current_indicator: 5,
        flat_current_event: 21,
        up_outlook: 5,
        down_outlook: 6,
        positive_outlook_indicator: 7,
        positive_outlook_event: 0,
        negative_outlook_indicator: 4,
        negative_outlook_event: 0,
        flat_outlook_indicator: 3,
        flat_outlook_event: 4,
    },
    {
        concept: 'apple',
        date: '2023-06-11',
        topics: [
            'market',
            'stock_price',
            'demand',
            'revenue',
        ],
        current_indicator: 88,
        current_event: 16,
        outlook_indicator: 20,
        outlook_event: 0,
        up_current: 8,
        down_current: 8,
        positive_current_indicator: 8,
        positive_current_event: 7,
        negative_current_indicator: 8,
        negative_current_event: 1,
        flat_current_indicator: 1,
        flat_current_event: 8,
        up_outlook: 7,
        down_outlook: 2,
        positive_outlook_indicator: 7,
        positive_outlook_event: 0,
        negative_outlook_indicator: 2,
        negative_outlook_event: 0,
        flat_outlook_indicator: 1,
        flat_outlook_event: 0,
    },
];

export const exampleDaasUnified = [
    [
        'alibaba-stock_price',
        '2023-06-12',
        0,
        13,
        0,
        2,
        1,
        2,
        1,
        1,
        0,
        0,
        0,
        0,
        0,
    ],
    [
        'alibaba-stock_price',
        '2023-06-11',
        0,
        10,
        0,
        0,
        1,
        0,
        1,
        1,
        0,
        0,
        0,
        0,
        0,
    ],
    [
        'alibaba-stock_price',
        '2023-06-10',
        0,
        12,
        7,
        1,
        0,
        1,
        0,
        2,
        4,
        0,
        4,
        0,
        1,
    ],
];

export const exampleSummary = [
    {
        id: 38332,
        type: 'kb',
        version: '202306022225',
        latest: 0,
        current: 0,
        creator: 'aldo.martinez@causalitylink.com',
        created: '2023-06-08T16:37:53.000Z',
        actor: 'aldo.martinez@causalitylink.com',
        updated: '2023-06-08T16:37:53.000Z',
        export: 0,
        custom: 0,
        deleted: null,
        tags: null,
    },
    {
        id: 38331,
        type: 'kb',
        version: '202305122152',
        latest: 1,
        current: 1,
        creator: 'eric@causalitylink.com',
        created: '2023-05-13T17:03:11.000Z',
        actor: 'eric@causalitylink.com',
        updated: '2023-05-13T17:04:29.000Z',
        export: 1,
        custom: 1,
        deleted: null,
        tags: 'V3.6.2',
    },
    {
        id: 38330,
        type: 'prepr',
        version: '202305081856',
        latest: 1,
        current: 1,
        creator: 'eric@causalitylink.com',
        created: '2023-05-13T17:03:11.000Z',
        actor: 'eric@causalitylink.com',
        updated: '2023-05-13T17:04:22.000Z',
        export: 1,
        custom: 1,
        deleted: null,
        tags: 'V3.6.2',
    },
    {
        id: 38293,
        type: 'kb',
        version: '202305041314',
        latest: 0,
        current: 0,
        creator: 'eric@causalitylink.com',
        created: '2023-05-05T17:59:40.000Z',
        actor: 'eric@causalitylink.com',
        updated: '2023-05-05T18:00:11.000Z',
        export: 0,
        custom: 0,
        deleted: null,
        tags: 'V3.6.1',
    },
    {
        id: 38292,
        type: 'prepr',
        version: '202305011406',
        latest: 0,
        current: 0,
        creator: 'eric@causalitylink.com',
        created: '2023-05-05T17:59:40.000Z',
        actor: 'eric@causalitylink.com',
        updated: '2023-05-05T18:00:01.000Z',
        export: 0,
        custom: 0,
        deleted: null,
        tags: 'V3.6.1',
    },
];

export const exampleScheduler = { message: 'Scheduling has begun' };

export type GenericMap = { [ key: string ]: string };

// A simple narrowed 'any', to the types that can be auto-generated
export type TApiRequestObject = { [ key: string ]: number | string | boolean };

// Structure mounted endpoint details to help track format
// In shared because both server and front end code
export interface IApiHostInfo {
    name: string; // API
    // path should probably be renamed to something like str, but we need a way to refresh
    //  existing local storage if it is renamed
    path: string; // usually /api or http://127.0.0.1:3000
}

// Communication with OpenAI
export interface IChatResponse {
    model: string;
    error?: { code: string, message: string };
    choices: { index: number, message: { content: string, role: string }, finish_reason: string }[],
    created: number;
    id: string;
    object: string;
    usage: { prompt_tokens: number, completion_tokens: number, total_tokens: number }
}

// This is a gzip stream compatible writer + generic handler required methods
export interface IResponseWithStreamingHandlers {
    json?: (GenericMap) => void;
    send?: (GenericMap) => void;
    setHeader: (key: string, value: string) => void;
}

export const exampleSpacyInterpret: ISpacyInterpret = {
    parsedText: [
        {
            dep_: 'ROOT',
            ent_id_: '',
            ent_iob_: 'O',
            ent_type_: '',
            has_vector: true,
            head: 'greater',
            i: 0,
            idx: 0,
            is_alpha: true,
            is_ascii: true,
            is_bracket: false,
            is_currency: false,
            is_digit: false,
            is_left_punct: false,
            is_lower: true,
            is_oov: false,
            is_punct: false,
            is_quote: false,
            is_right_punct: false,
            is_sent_start: true,
            is_space: false,
            is_stop: false,
            is_title: false,
            lemma_: 'great',
            like_email: false,
            like_num: false,
            like_url: false,
            lower_: 'greater',
            norm_: 'greater',
            orth_: 'greater',
            pos_: 'ADJ',
            prefix_: 'g',
            prob: '-20.0',
            rank: 2184,
            sentiment: '0.0',
            shape_: 'xxxx',
            string: 'greater ',
            subtree: 'greater',
            suffix_: 'ter',
            tag_: 'JJR',
            text: 'greater',
            text_with_ws: 'greater ',
            vector_norm: '5.9849725',
            whitespace_: ' ',
            head_idx: null,
        },
    ],
};

export const exampleStemResponse: IStemResponse = {
    result: 'price',
    similarElements: [
        {
            id: 48183,
            translation: 'prices',
            mustBe: '',
            specificContext: null,
        },
        {
            id: 1271,
            translation: 'prices',
            mustBe: 'NOUN',
            specificContext: null,
        },
    ],
};

export const exampleArticlesSear = {
    total: 2627679,
    articles: [
        {
            id: 'H8e83IcBLeC4J8klHlPm',
            link: 'https://seekingalpha.com/article/4424528-sa-pro-editors-plus500-65-percent-upside?source=feed_symbol_PLSQF',
            symbol: [
                'PLSQF',
            ],
            title: 'SA PRO Editors: Plus500 - 65% Upside',
            publishedDate: '2106-02-07T06:28:15.000Z',
            publisher: 'sa',
            author: 'SA PRO Editors',
        },
        {
            id: '_0SvT4gBDfMVi3bNRlSm',
            link: 'https://www.investopedia.com/selena-gomez-net-worth-7503282',
            symbol: [
                'DIA',
            ],
            title: 'Selena Gomez Will Host Two New Food Network Shows—Here’s How She Made Her Millions',
            publishedDate: '2023-05-24T21:34:26.935Z',
            publisher: 'ip',
            author: '',
        },
        {
            id: 'CkTET4gBDfMVi3bNVlWN',
            link: 'https://www.thestreet.com/investing/stocks/apple-aapl-stock-two-buy-the-dip-setups-technical-analysis',
            symbol: [
                'F',
            ],
            title: 'Apple Stock Has Two Buy-the-Dip Spots (and One Is Really Attractive)',
            publishedDate: '2023-05-24T16:22:00.000Z',
            publisher: 'ts',
        },
        {
            id: 'AESwT4gBDfMVi3bNsVUY',
            link: 'https://seekingalpha.com/article/4606921-ford-stock-high-pessimism-creates-buying-opportunity',
            symbol: [
                'F.PB',
                'FPD',
                'F',
            ],
            title: 'Ford: High Pessimism Creates A Fantastic Buying Opportunity',
            publishedDate: '2023-05-24T12:00:09.000Z',
            publisher: 'sa',
            author: 'JR Research',
        },
        {
            id: 'AUSxT4gBDfMVi3bNTFUj',
            link: 'https://seekingalpha.com/article/3974473-anglogold-ashanti-q1-2016-consistent-operating-and-financial-performance',
            symbol: [
                'F',
                'AU',
            ],
            title: 'Ford recalls over 175,000 vehicles due to seatbelt issue',
            publishedDate: '2023-05-24T09:46:52.000Z',
            publisher: 'sa',
            author: 'Kevin P. Curran',
        },
        {
            id: 'AkSyT4gBDfMVi3bNzlVJ',
            link: 'https://seekingalpha.com/article/4606931-ford-i-love-boring-5-2-percent-yield',
            symbol: [
                'F',
            ],
            title: 'Ford: I Love The Boring 5.2% Yield',
            publishedDate: '2023-05-24T07:26:23.000Z',
            publisher: 'sa',
            author: 'The Asian Investor',
        },
        {
            id: '_kStT4gBDfMVi3bNo1S2',
            link: 'https://edition.cnn.com/2023/05/24/economy/moodys-default-debt-ceiling/index.html',
            symbol: [
                'DIA',
            ],
            title: 'undefined',
            publishedDate: '2023-05-24T00:00:00.000Z',
            publisher: 'nn',
            author: 'Matt Egan',
        },
        {
            id: '-kSrT4gBDfMVi3bNTlTg',
            link: 'https://www.cnbc.com/2023/05/23/former-fed-chair-ben-bernanke-says-theres-more-work-ahead-to-control-inflation.html',
            symbol: [
                'DIA',
            ],
            title: 'Former Fed Chair Ben Bernanke says there\'s more work ahead to control inflation',
            publishedDate: '2023-05-23T19:01:54.000Z',
            publisher: 'cn',
            author: 'Jeff Cox',
        },
        {
            id: 'A0SzT4gBDfMVi3bNHFUA',
            link: 'https://seekingalpha.com/article/3973941-arrowheads-arwr-ceo-christopher-anzalone-on-q2-2016-results-earnings-call-transcript',
            symbol: [
                'F',
                'SQM',
                'CMP',
                'ALB',
                'TSLA',
                'LTHM',
                'ARWR',
            ],
            title: 'Ford looks to lower costs, tap into subscription revenue stream',
            publishedDate: '2023-05-22T19:41:20.000Z',
            publisher: 'sa',
            author: 'Kevin P. Curran',
        },
        {
            id: 'BES0T4gBDfMVi3bN4lVX',
            link: 'https://seekingalpha.com/article/4606562-wall-street-lunch-fords-capital-markets-day-with-david-alton-clark-bullishness-intact',
            symbol: [
                'F',
                'BYDDF',
                'BYDDY',
                'F.PB',
                'FPD',
                'GM',
                'TSLA',
            ],
            title: 'Ford Capital Markets Day With David Alton Clark - Bullishness Intact',
            publishedDate: '2023-05-22T19:40:00.000Z',
            publisher: 'sa',
            author: 'Wall Street Breakfast',
        },
    ],
    timedOut: false,
};

export const exampleDaasMention = {
    rawData: [
        [
            'alibaba-stock_price',
            'mw_w9SgsYgBP1QfIiQ0iOyv/en-b-000-000-41',
            '2023-06-12T21',
            0,
            1,
            0.3591,
            0,
            'alibaba-*-internet_direct_marketing_retail-*-*-*-stock_price',
        ],
        [
            'alibaba-stock_price',
            'mw_w9SgsYgBP1QfIiQ0iOyv/en-t-000-000-31',
            '2023-06-12T21',
            0,
            1,
            3,
            1,
            'alibaba-*-internet_direct_marketing_retail-*-*-*-stock_price',
        ],
        [
            'alibaba-stock_price',
            'mw_w9SgsYgBP1QfIiQ0iOyv/en-b-001-000-396',
            '2023-06-12T21',
            0,
            0,
            0,
            0,
            'alibaba-*-internet_direct_marketing_retail-*-*-*-stock_price',
        ],
        [
            'alibaba-stock_price',
            'fo_1dhIsIgBX6q1E4tA59J4/en-b-000-001-162',
            '2023-06-12T15',
            0,
            -1,
            -3,
            0,
            'alibaba-*-internet_direct_marketing_retail-*-*-*-stock_price',
        ],
    ],
    mentionType: 'trends',
    conceptKey: 'alibaba-stock_price',
};

export const exampleBulkSynonymsTest: IBulkSynonymsTest[] = [
    {
        line: 'africa, test 1',
        data: {
            sample: 'test 1',
            translation: 'africa',
            phrase: 'test 1',
        },
    },
    {
        line: 'africa, test 2',
        data: {
            sample: 'test 2',
            translation: 'africa',
            phrase: 'test 2',
        },
    },
];

export const exampleBulkSynonymsInsert: IBulkSynonymsInsert[] = [
    {
        line: 'africa,test 1',
    },
    {
        line: 'africa,test 2',
    },
];

export const exampleSymbolMatches: ISymbolMatches[] = [ {
    symbol: 'AAPL',
    exchange: 'NYSE',
    error: 'Unexpected format',
    data: [ {
        marker: 'apple',
        ticker: 'NYSE:AAPL',
        label: 'Apple Inc.',
        type: 'companyGroup',
    } ],
} ];

export const exampleMetaDataChunked = {
    bodyMeta: 211,
    titleMeta: 2,
    mergeStats: [
        {
            added: 0,
            delta: [ Object ],
            removed: 0,
            unchanged: 226,
            updated: 0,
        },
    ],
    message: 'Success',
    stats: {
        authors: 0,
        baseEvents: 14,
        baseKPIs: 80,
        companyGroups: 2,
        ecls: 0,
        events: 0,
        indicators: 22,
        industryGroups: 64,
        locations: 2,
        segments: 0,
        suggestions: 30,
        trends: 2,
        feedbacks: 0,
        features: 10,
    },
    logHistory: false,
};

export const exampleForgot = { message: 'Noted. If the username exists in our records, we\'ll send recovery instructions to your account\'s email' };

export const exampleMultiUser: string[][] = [ [ 'Smith,John,john.smith@gmail.com', 'Pass' ], [ 'Smith,Laura,laurasmith@gmail.com', 'Pass' ] ];

export const exampleSeedUser = true;

export const exampleNewUser = true;

export const exampleLogin = {
    message: 'Success',
    token: 'eyJ1c2VyIjp7InVzZXJuYW1lIjoiYWxkby5tYXJ0aW5lekBjYXVzYWxpdHlsaW5rLmNvbSIsImZpcnN0IjoiQWxkbyIsImxhc3QiOiJNYXJ0w61uZXoiLCJlbWFpbCI6ImF' +
    'sZG8ubWFydGluZXpAY2F1c2FsaXR5bGluay5jb20ifSwicm9sZXrtyhfgMiOlsiQ0xBZG1pbiJdLCJzdWJzY3JpYmVkUHVibGlzaGVycyI6W10sInJlY2VudExvZ2luIjp7ImlwIjoiMTcyL' +
    'jMxLjE3Ni4xIiwidXNlcm5hbWUiOiJhbGRvLm1hcnRpbmV6QGNhdfghfghXNhbGl0eWxpbmsuY29tIiwidGltZXN0YW1wIjoiMjAyMy0wNi0wOVQxNToxNzoxNS43NTFaIiwic3VjY2Vzc2Z' +
    '1bCI6dHJ1ZX0sImlhdCI6MTY4NjMyNDQwOC45rthrwiZXhwIjoxNjg2MzUzMjA4fQ.enKtsZ_lKITkl2dQdMA6GAJbfrAW9hTZ2js9T-crln8',
};

export const examplePdf = 'Text extracted from a pdf';

export interface IEnableData {
    enableIEX: boolean;
}
